'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings, X, Upload, Loader2 } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { coverGradient } from '@/lib/marketplace/cover';
import { lucideMark, LUCIDE_MARK_NAMES } from '@/components/workspace/panels/content-preview';
import { announceContentRenamed } from '@/components/workspace/item-label';
import { cn } from '@/lib/utils';

// Same swatch palette as the published listing's Manage drawer (manage-app.tsx)
// so an app keeps the same color options before and after publishing.
const COVER_SWATCHES = ['#6366F1', '#7C3AED', '#2563EB', '#0D9488', '#22C55E', '#F59E0B', '#EF4444', '#EC4899', '#0EA5E9', '#64748B'];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

/** What Save writes; handed back through `onSaved` so the parent panel and
 *  header repaint without a refetch. */
export interface DraftIdentity {
  name: string;
  description: string | null;
  /** Bare lucide name (no `lucide:` prefix), or null for the default mark. */
  icon: string | null;
  color: string | null;
  bannerUrl: string | null;
  logoUrl: string | null;
}

/**
 * Manage drawer for an UNPUBLISHED app — the draft counterpart of the
 * published listing's ManageApp drawer, same right-side slide-over chrome.
 * Edits land on the widget CONTENT row (PATCH /api/content/[id] `identity`),
 * which is exactly what the cards, the draft store page, and a later publish
 * read: publishing seeds the listing channel from these fields, so branding
 * done here carries over. No moderators / categories / visibility sections;
 * those are channel concepts that begin at publish.
 */
export function DraftManageDrawer({
  contentId,
  seed,
  identity,
  onSaved,
  defaultOpen,
}: {
  contentId: string;
  /** Gradient seed (appMarkSeed) — the app's stable color identity. */
  seed: string;
  identity: DraftIdentity;
  onSaved: (saved: DraftIdentity) => void;
  /** Mount with the drawer already open (context-menu "Manage" entry). */
  defaultOpen?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(!!defaultOpen);
  const [name, setName] = useState(identity.name);
  const [description, setDescription] = useState(identity.description ?? '');
  const [icon, setIcon] = useState<string | null>(identity.icon);
  const [color, setColor] = useState<string | null>(identity.color);
  const [bannerUrl, setBannerUrl] = useState<string | null>(identity.bannerUrl);
  const [logoUrl, setLogoUrl] = useState<string | null>(identity.logoUrl);
  const [uploading, setUploading] = useState<null | 'cover' | 'logo'>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Re-seed the form whenever the drawer opens: the parent's state may have
  // moved (an earlier save, a different app in the same mounted panel).
  useEffect(() => {
    if (!open) return;
    setName(identity.name);
    setDescription(identity.description ?? '');
    setIcon(identity.icon);
    setColor(identity.color);
    setBannerUrl(identity.bannerUrl);
    setLogoUrl(identity.logoUrl);
    setError(null);
  }, [open, identity]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy]);

  // Same upload path as the published drawer (and settings avatars).
  const uploadImage = async (file: File, kind: 'cover' | 'logo') => {
    if (!file.type.startsWith('image/')) { setError('Please choose an image file (PNG, JPG, GIF, WebP).'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('Image must be under 20MB.'); return; }
    setUploading(kind);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'post');
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.error || `Upload failed (${res.status})`);
      if (kind === 'cover') setBannerUrl(data.url as string);
      else setLogoUrl(data.url as string);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/content/${contentId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          displayName: name.trim() || identity.name,
          identity: {
            description: description.trim() || null,
            icon: icon ? `lucide:${icon}` : null,
            color,
            bannerUrl,
            logoUrl,
          },
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Save failed (${res.status})`);
      // The app's windows on the canvas are labelled from this tab's live
      // content cache, which `onSaved` does not reach; the announcement
      // refreshes it so the rail and Stage follow the rename without a
      // reload (item-label.ts, 2026-09-13).
      announceContentRenamed(contentId, name.trim() || identity.name);
      onSaved({
        name: name.trim() || identity.name,
        description: description.trim() || null,
        icon,
        color,
        bannerUrl,
        logoUrl,
      });
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const coverCss = coverGradient(seed, color).css;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
      >
        <Settings className="h-4 w-4" />
        {t('user.manage')}
      </button>

      {open && typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[200]">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" onClick={() => !busy && setOpen(false)} />
            <div className="absolute right-0 top-0 bottom-0 flex w-[400px] max-w-[94vw] flex-col border-l border-border/60 bg-background shadow-2xl">
              {/* Header */}
              <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-4 py-3">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span className="truncate text-sm font-semibold">Manage {identity.name}</span>
                <button
                  type="button"
                  aria-label={t('workspace.canvas.node.close')}
                  onClick={() => !busy && setOpen(false)}
                  className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <SectionLabel>Details</SectionLabel>
                <label className="mb-1 block text-[11px] text-muted-foreground">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mb-3 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                />
                <label className="mb-1 block text-[11px] text-muted-foreground">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="What does this app do?"
                  className="mb-3 w-full resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                />

                {/* Icon — the app's mark, from the product's lucide vocabulary. */}
                <label className="mb-1 block text-[11px] text-muted-foreground">Icon</label>
                <div className="mb-3 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => setIcon(null)}
                    className={cn(
                      'flex h-8 items-center rounded-md border px-2 text-[11px] transition-colors hover:bg-accent',
                      icon === null ? 'border-foreground' : 'border-border/40 text-muted-foreground',
                    )}
                  >
                    Auto
                  </button>
                  {LUCIDE_MARK_NAMES.map((n) => {
                    const Icon = lucideMark({ kind: 'lucide', name: n });
                    if (!Icon) return null;
                    return (
                      <button
                        key={n}
                        type="button"
                        aria-label={`Icon ${n}`}
                        title={n}
                        onClick={() => setIcon(n)}
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-accent',
                          icon === n ? 'border-foreground ring-2 ring-foreground/30' : 'border-border/40 text-muted-foreground',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>

                {/* Cover — uploaded image, or the generated gradient + accent. */}
                <label className="mb-1 block text-[11px] text-muted-foreground">Cover</label>
                <div
                  className="mb-2 h-16 w-full overflow-hidden rounded-lg"
                  style={{ background: bannerUrl ? undefined : coverCss }}
                >
                  {bannerUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={bannerUrl} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploading === 'cover'}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    {uploading === 'cover' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    {bannerUrl ? t('marketplace.replace') : t('marketplace.uploadImage')}
                  </button>
                  {bannerUrl ? (
                    <button
                      type="button"
                      onClick={() => setBannerUrl(null)}
                      className="rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Remove
                    </button>
                  ) : (
                    <>
                      <span className="mx-0.5 h-4 w-px bg-border/60" />
                      {COVER_SWATCHES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          aria-label={`Cover ${c}`}
                          onClick={() => setColor(c)}
                          className={cn(
                            'h-6 w-6 rounded-full border transition-transform hover:scale-110',
                            color === c ? 'border-foreground ring-2 ring-foreground/30' : 'border-border/40',
                          )}
                          style={{ background: c }}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => setColor(null)}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-[11px] transition-colors hover:bg-accent',
                          color === null ? 'border-foreground' : 'border-border/40 text-muted-foreground',
                        )}
                      >
                        Auto
                      </button>
                    </>
                  )}
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadImage(f, 'cover'); e.target.value = ''; }}
                  />
                </div>

                {/* Thumbnail — the square art inside the mark chip. */}
                <label className="mb-1 block text-[11px] text-muted-foreground">Thumbnail</label>
                <div className="mb-3 flex items-center gap-2">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/40"
                    style={{ background: logoUrl ? undefined : coverCss }}
                  >
                    {logoUrl && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={logoUrl} alt="" className="h-full w-full object-cover" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading === 'logo'}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    {uploading === 'logo' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    {logoUrl ? 'Replace' : 'Upload'}
                  </button>
                  {logoUrl && (
                    <button
                      type="button"
                      onClick={() => setLogoUrl(null)}
                      className="rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Remove
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadImage(f, 'logo'); e.target.value = ''; }}
                  />
                </div>

                {error && <p className="mb-3 text-xs text-red-500">{error}</p>}

                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={busy}
                  className="w-full rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Save details'}
                </button>

                <p className="mt-3 text-[11px] leading-snug text-muted-foreground">
                  These details show on your app card and its page, and seed the public listing when you publish.
                </p>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
